import { Breadcrumb, HeaderToolbar, Skeleton } from "@xola/ui-kit";
import clsx from "clsx";
import { isUndefined } from "lodash";
import React, { ReactNode, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Experience } from "types";
import { NoTranslate } from "../../components/Common/NoTranslate";
import { SidebarTabs } from "../../components/Common/SidebarTabs";
import { ColumnLayout } from "../../components/Layout/ColumnLayout";
import { useResource } from "../../hooks/useResource";
import { Page } from "../Page";
import { useExperienceTabs } from "./useExperienceTabs";

export const ExperiencePage = () => {
    const history = useHistory();
    const { id, section } = useParams<{ id?: ID; section?: string }>();

    const { data: experience } = useResource<Experience>(["experiences", id], null, {
        staleTime: 1000, // Cache for 1s so that its always re-fetched when the page loads
        keepPreviousData: true,
    });

    const tabs = useExperienceTabs(experience);
    const initialSection = section ?? id ? tabs[1].id : tabs[0].id;
    const initialActiveIndex = tabs.findIndex((tab) => tab.id === section ?? initialSection);
    const [activeTabIndex, setActiveTabIndex] = useState(initialActiveIndex === -1 ? 0 : initialActiveIndex);

    useEffect(() => {
        if (section) {
            const newIndex = tabs.findIndex((tab) => tab.id === section);
            if (newIndex >= 0 && activeTabIndex !== newIndex) {
                setActiveTabIndex(newIndex);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [section]);

    const breadcrumbs: Array<{ label: ReactNode; url?: string; tabIndex?: number }> = [
        { label: "Products", url: "/products/experiences" },
        { label: "Experiences", url: "/products/experiences" },
        { label: experience ? <NoTranslate>{experience.name}</NoTranslate> : "New", tabIndex: 1 },
        { label: tabs[activeTabIndex].label, tabIndex: activeTabIndex },
    ];

    const handleBreadcrumbClick = (index: number, url?: string) => {
        if (url) {
            history.push(url);
        } else {
            handleActiveTabChange(index);
        }
    };

    const handleActiveTabChange = (index: number) => {
        if (id) {
            const path = `/products/experiences/${id}/${tabs[index].id}`;
            history.push(path);
        } else {
            setActiveTabIndex(index);
        }
    };

    return (
        <Page isDynamicTitle={!!experience} title={experience ? experience.name : "Create Experience"}>
            <ColumnLayout>
                <HeaderToolbar classNames="justify-between">
                    <HeaderToolbar.Breadcrumb className="flex">
                        {breadcrumbs.map((breadcrumb) => (
                            <Breadcrumb.Item
                                key={breadcrumb.label}
                                className="!inline-block max-w-24 truncate lg:max-w-50 2xl:max-w-150"
                                onClick={() => handleBreadcrumbClick(breadcrumb.tabIndex ?? 1, breadcrumb.url)}
                            >
                                {breadcrumb.label}
                            </Breadcrumb.Item>
                        ))}
                    </HeaderToolbar.Breadcrumb>
                    <div id="header-toolbar-actions" />
                </HeaderToolbar>

                <ColumnLayout.Fill>
                    <SidebarTabs activeTab={activeTabIndex} onClick={handleActiveTabChange}>
                        {tabs.map((tab) => {
                            const isHidden = !isUndefined(tab.condition) && !tab.condition;
                            return (
                                <SidebarTabs.Tab
                                    key={tab.id}
                                    name={tab.label}
                                    icon={tab.icon}
                                    description={tab.description}
                                    className={clsx(isHidden && "hidden")}
                                >
                                    {tab.content ?? (
                                        <div className="p-4">
                                            <Skeleton />
                                        </div>
                                    )}
                                </SidebarTabs.Tab>
                            );
                        })}
                    </SidebarTabs>
                </ColumnLayout.Fill>
            </ColumnLayout>
        </Page>
    );
};
